import type { FC } from "react";
import { useEffect } from "react";
import styled from "styled-components";
import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";
import { translate } from "app/filters/i18nFilter";
import { StructureModal } from "@similarweb/ui-components";
import { TextFieldTitle } from "app/components/React/TableSelectionComponents/segment-selection/segments-modal/Styled";
import { Textfield } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";
import { allTrackers } from "app/services/track/track";
import { useMarket } from "./useMarket";

const { carbon, blue } = colorsPalettes;

const BodyContainer = styled.div`
    margin-top: 16px;
`;

const IndustryDisclaimer = styled.div`
    padding: 16px;
    ${setFontVariant("body2", carbon[400])};
    background-color: ${rgba(blue[400], 0.1)};
    border: 1px solid ${blue[300]};
    margin-bottom: 40px;
    border-radius: 3px;
`;

const TextFieldContainer = styled.div`
    min-height: 64px;
`;

interface ICustomIndustryModal {
    showModal: boolean;
    setShowModal: (showModal: boolean) => void;
    segmentGroupName: string;
    segmentGroupId: string;
}

const CustomIndustryModal: FC<ICustomIndustryModal> = ({
    showModal,
    setShowModal,
    segmentGroupName,
    segmentGroupId,
}) => {
    const {
        marketName,
        onUpdateMarketName,
        hasErrorWithName,
        nameErrorMessage,
        allowSave,
        setAllowSave,
        isLoading,
        saveMarket,
    } = useMarket(segmentGroupName);
    useEffect(() => {
        if (showModal) {
            setAllowSave(Boolean(segmentGroupName));
            onUpdateMarketName(segmentGroupName);
        }
    }, [segmentGroupName, showModal]);

    const onChange = (name: string) => {
        onUpdateMarketName(name);
        setAllowSave(true);
    };

    const viewIndustryClick = () => {
        allTrackers.dispatchTrackEvent("Pop up", "click", `convert into industry save clicked`);
        saveMarket(marketName, segmentGroupId);
    };

    const cancelBtnClick = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "click", `cancel convert into industry`);
        setShowModal(false);
    };

    return (
        <div data-automation="CustomIndustryModal">
            <StructureModal
                isOpen={showModal}
                title={translate("segments.page.comparison.convert.into.industry.modal.title")}
                mainButton={{
                    label: translate("segments.page.comparison.convert.into.industry.modal.btn.view"),
                    onClick: () => viewIndustryClick(),
                    type: "primary",
                    tooltipEnabled: false,
                    isLoading,
                    isDisabled: !allowSave || hasErrorWithName,
                }}
                additionalButton={{
                    label: translate("segments.page.comparison.convert.into.industry.modal.btn.cancel"),
                    onClick: () => cancelBtnClick(),
                    type: "flatDark",
                    tooltipEnabled: false,
                }}
                customStyles={{ content: { width: "424px" } }}
            >
                <BodyContainer>
                    <TextFieldTitle>
                        {translate("segments.page.comparison.convert.into.industry.modal.subtitle")}
                    </TextFieldTitle>
                    <TextFieldContainer>
                        <Textfield
                            error={hasErrorWithName}
                            errorMessage={nameErrorMessage}
                            onChange={onChange}
                            placeholder={translate("segments.popup.wizard.body.name.placeholder")}
                            defaultValue={segmentGroupName}
                            loadingComponent={<DotsLoader />}
                            isLoading={isLoading}
                            disabled={isLoading}
                        />
                    </TextFieldContainer>
                    <IndustryDisclaimer>
                        {translate("segments.page.comparison.convert.into.industry.modal.disclaimer")}
                    </IndustryDisclaimer>
                </BodyContainer>
            </StructureModal>
        </div>
    );
};

export default CustomIndustryModal;
